import React, { useState, useEffect, useContext } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell, FormControlLabel,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Stack,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    IconButton,
    Tooltip,
    Modal,
    CircularProgress,
    Checkbox,
    Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import LockIcon from "@mui/icons-material/Lock";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";

// Styled Paper
const StyledPaper = styled(Paper)(({ theme, mode, customColors }) => ({
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    backgroundColor:
        mode === "dark"
            ? "#2A2A3C"
            : mode === "custom"
                ? customColors?.background || "#f5f5f5"
                : "#f0f7ff",
    borderRadius: "8px",
    boxShadow:
        mode === "dark"
            ? "0 2px 8px rgba(0,0,0,0.6)"
            : "0 2px 8px rgba(0,0,0,0.15)",
}));

// Palette setup
const palette = {
    light: {
        headerBg: "#e3f2fd",
        headerText: "#37474f",
        rowHover: "#f5f5f5",
        text: "#333",
    },
    dark: {
        headerBg: "#2A2A3C",
        headerText: "#EDEDED",
        rowHover: "#383C54",
        text: "#EDEDED",
    },
    custom: (customColors) => ({
        headerBg: customColors?.cardBg || "#f5f5f5",
        headerText: customColors?.text || "#111111",
        rowHover: customColors?.primary || customColors?.primary || "#e8f4ff",
        text: customColors?.text || "#111111",
    }),
};

export default function SetPermissionsPanel({
    mode,
    customColors,
    setSnackbar,
    isLoading,
    setIsLoading,
    currentUserRole,
    myPermissions,
}) {
    // ========== STATE ==========
    const [selectedCustomRole, setSelectedCustomRole] = useState("");
    const [customPermissions, setCustomPermissions] = useState({});
    const [tempCustomPermissions, setTempCustomPermissions] = useState({});
    const [customRolesDropdown, setCustomRolesDropdown] = useState([]);
    const [permissionMode, setPermissionMode] = useState("custom");
    // values: "all" | "custom"

    const apiUrl = process.env.REACT_APP_BASE_URL;

    // ========== EFFECTS ==========
    // Fetch custom roles for dropdown
    useEffect(() => {
        const fetchCustomRolesForDropdown = async () => {
            setIsLoading(true);
            try {
                const token = sessionStorage.getItem("authToken");
                const response = await axios.get(`${apiUrl}/custom-roles/`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const mappedRoles = Array.isArray(response.data)
                    ? response.data.map((role) => ({
                        id: role.id,
                        name: role.name,
                        permissions: role.permissions || {},
                        created_by: role.created_by,
                    }))
                    : Array.isArray(response.data.roles)
                        ? response.data.roles.map((role) => ({
                            id: role.id,
                            name: role.name,
                            permissions: role.permissions || {},
                            created_by: role.created_by,
                        }))
                        : [];
                setCustomRolesDropdown(mappedRoles);
            } catch (error) {
                console.error("Failed to fetch custom roles for dropdown:", error);
                setSnackbar({
                    open: true,
                    message: error.response?.data?.error || error.response?.data?.message || "Failed to fetch custom roles. Please try again.",
                    severity: "error",
                });
                setCustomRolesDropdown([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchCustomRolesForDropdown();
    }, [setIsLoading, setSnackbar]);

    // Fetch custom permissions when selectedCustomRole changes
    useEffect(() => {
        if (selectedCustomRole) {
            const fetchCustomPermissions = async () => {
                setIsLoading(true);
                try {
                    const token = sessionStorage.getItem("authToken");
                    const response = await axios.get(
                        `${apiUrl}/custom-roles/view-permissions/?role_name=${selectedCustomRole}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const { role_id, role_name, permissions, ...relevantPermissions } = response.data;
                    setCustomPermissions(relevantPermissions);
                    setTempCustomPermissions(relevantPermissions);
                } catch (error) {
                    console.error("Failed to fetch custom permissions:", error);
                    let errorMessage = error.response?.data?.error || error.response?.data?.message || "Failed to fetch custom permissions. Please try again.";
                    let severity = "error";
                    if (errorMessage === "Permission denied: you cannot can_view_custom_role_permission.") {
                        errorMessage = "Permission denied: you do not have permission to view custom role permissions.";
                        severity = "warning";
                    }
                    setSnackbar({
                        open: true,
                        message: errorMessage,
                        severity: severity,
                    });
                    setCustomPermissions({});
                    setTempCustomPermissions({});
                } finally {
                    setIsLoading(false);
                }
            };
            fetchCustomPermissions();
        }
    }, [selectedCustomRole, setIsLoading, setSnackbar]);

    // ========== HANDLERS ==========
    const handleCustomPermissionChange = (key) => (event) => {
        setTempCustomPermissions((prev) => ({
            ...prev,
            [key]: event.target.checked,
        }));
    };

    const handleDisabledAction = () => {
        setSnackbar({
            open: true,
            message: "You do not have permission to perform this action.",
            severity: "warning",
        });
    };

    const handleCustomPermissionsUpdate = async () => {
        if (!selectedCustomRole) {
            setSnackbar({
                open: true,
                message: "Please select a custom role.",
                severity: "warning",
            });
            return;
        }
        try {
            const token = sessionStorage.getItem("authToken");
            const response = await axios.post(
                `${apiUrl}/custom-roles/set-permissions/`,
                {
                    role_name: selectedCustomRole,
                    permissions: tempCustomPermissions,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const { role_id, role_name, permissions, ...relevantPermissions } = response.data;
            setCustomPermissions(relevantPermissions);
            setTempCustomPermissions(relevantPermissions);
            setSnackbar({
                open: true,
                message: "Custom permissions updated successfully.",
                severity: "success",
            });
            // Re-fetch permissions to ensure sync
            const fetchCustomPermissions = async () => {
                setIsLoading(true);
                try {
                    const response = await axios.get(
                        `${apiUrl}/custom-roles/view-permissions/?role_name=${selectedCustomRole}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const { role_id, role_name, permissions, ...fetchedPermissions } = response.data;
                    setCustomPermissions(fetchedPermissions);
                    setTempCustomPermissions(fetchedPermissions);
                } catch (error) {
                    console.error("Failed to re-fetch custom permissions:", error);
                    let errorMessage = error.response?.data?.error || error.response?.data?.message || "Failed to fetch custom permissions. Please try again.";
                    let severity = "error";
                    if (errorMessage === "Permission denied: you cannot can_view_custom_role_permission.") {
                        errorMessage = "Permission denied: you do not have permission to view custom role permissions.";
                        severity = "warning";
                    }
                    setSnackbar({
                        open: true,
                        message: errorMessage,
                        severity: severity,
                    });
                    setCustomPermissions({});
                    setTempCustomPermissions({});
                } finally {
                    setIsLoading(false);
                }
            };
            fetchCustomPermissions();
        } catch (error) {
            console.error("Failed to update custom permissions:", error);
            let errorMessage = error.response?.data?.error || error.response?.data?.message || "Failed to update custom permissions. Please try again.";
            let severity = "error";
            if (errorMessage === "Permission denied: you cannot can_set_custom_role_permission.") {
                errorMessage = "Permission denied: you do not have permission to set custom role permissions.";
                severity = "warning";
            }
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: severity,
            });
        }
    };

    // Helper function to group permissions by resource with CRUD separation
    const groupPermissionsByResource = (permissions) => {
        const crudGroups = {};
        const otherGroups = {};
        const crudActions = ['create', 'read', 'update', 'delete'];

        Object.keys(permissions).forEach(key => {
            if (key === 'id' || key === 'role') return;

            // Extract resource and action
            const match = key.match(/can_(create|read|update|delete|view|start|submit|register|change|set|import|export|reset|bulk|enable)_(.+)/);
            if (match) {
                const [, action, resource] = match;
                const resourceKey = resource.replace(/_/g, ' ');

                if (crudActions.includes(action)) {
                    // CRUD operations
                    if (!crudGroups[resourceKey]) {
                        crudGroups[resourceKey] = {};
                    }
                    crudGroups[resourceKey][action] = { key, value: permissions[key] };
                } else {
                    // Other operations
                    if (!otherGroups[resourceKey]) {
                        otherGroups[resourceKey] = {};
                    }
                    otherGroups[resourceKey][action] = { key, value: permissions[key] };
                }
            }
        });

        return { crudGroups, otherGroups };
    };
    // Enable / Disable ALL CRUD permissions
    const toggleAllPermissions = (checked) => {
        setTempCustomPermissions((prev) => {
            const updated = {};

            Object.keys(prev).forEach((key) => {
                // ignore meta keys if any
                if (key === "id" || key === "role") return;

                updated[key] = checked;
            });

            return updated;
        });
    };

    // ========== RENDER ==========
    return (
        <>
            <StyledPaper
                sx={{
                    backgroundColor: mode === "dark"
                        ? palette.dark.headerBg
                        : mode === "custom"
                            ? customColors?.background || "#f5f5f5"
                            : palette.light.headerBg,
                    borderRadius: "16px",
                    boxShadow: mode === "dark"
                        ? "0 8px 24px rgba(0, 0, 0, 0.3)"
                        : "0 8px 24px rgba(30, 139, 175, 0.12)",
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        mb: 3,
                        fontWeight: 700,
                        letterSpacing: "-0.5px",
                    }}
                >
                    Manage Role Permissions
                </Typography>

                <FormControl
                    fullWidth
                    sx={{
                        mb: 4,
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            transition: "all 0.3s ease",
                            "&:hover": {
                                boxShadow: "0 4px 12px rgba(30, 139, 175, 0.15)",
                            },
                            "&.Mui-focused": {
                                boxShadow: "0 4px 16px rgba(30, 139, 175, 0.25)",
                            },
                        },
                    }}
                >
                    <InputLabel
                        id="custom-role-select-label"
                        sx={{
                            color: mode === "dark"
                                ? palette.dark.headerText
                                : mode === "custom"
                                    ? customColors?.text || "#111111"
                                    : palette.light.headerText,
                            fontWeight: 600,
                            "&.Mui-focused": {
                                color: "#1e8bafff",
                            },
                        }}
                    >
                        Select Custom Role
                    </InputLabel>
                    <Select
                        labelId="custom-role-select-label"
                        value={selectedCustomRole}
                        label="Select Custom Role"
                        onChange={(e) => setSelectedCustomRole(e.target.value)}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    maxHeight: "250px",
                                    width: "auto",
                                    minWidth: "200px",
                                    overflow: "auto",
                                    marginTop: "4px",
                                },
                            },
                            anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                            },
                            transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                            },
                            disablePortal: false,
                        }}
                        sx={{
                            width: "100%",
                            backgroundColor: mode === "dark"
                                ? palette.dark.headerBg
                                : mode === "custom"
                                    ? customColors?.background || "#ffffff"
                                    : "#ffffff",
                            color: mode === "dark"
                                ? palette.dark.headerText
                                : mode === "custom"
                                    ? customColors?.text || "#111111"
                                    : palette.light.headerText,
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: mode === "dark"
                                    ? "rgba(255, 255, 255, 0.2)"
                                    : "rgba(30, 139, 175, 0.3)",
                                borderWidth: "2px",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#1e8bafff",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#1e8bafff",
                            },
                        }}
                    >
                        <MenuItem value="">
                            <em>Select Custom Role</em>
                        </MenuItem>
                        {customRolesDropdown.map((role) => (
                            <MenuItem
                                key={role.id}
                                value={role.name}
                                sx={{
                                    color: mode === "dark"
                                        ? palette.dark.headerText
                                        : mode === "custom"
                                            ? customColors?.text || "#111111"
                                            : palette.light.headerText,
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    padding: "10px 16px",
                                    minHeight: "48px",
                                    "&:hover": {
                                        backgroundColor: mode === "dark"
                                            ? "rgba(30, 139, 175, 0.15)"
                                            : "rgba(30, 139, 175, 0.08)",
                                    },
                                    "&.Mui-selected": {
                                        backgroundColor: mode === "dark"
                                            ? "rgba(30, 139, 175, 0.2)"
                                            : "rgba(30, 139, 175, 0.12)",
                                        "&:hover": {
                                            backgroundColor: mode === "dark"
                                                ? "rgba(30, 139, 175, 0.25)"
                                                : "rgba(30, 139, 175, 0.15)",
                                        },
                                    },
                                }}
                            >
                                {role.name?.replace(/[^a-zA-Z0-9\s]/g, '')?.replace(/\s+/g, ' ').trim() || 'Unnamed Role'}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress sx={{ color: "#1e8bafff" }} size={48} />
                    </Box>
                ) : Object.keys(tempCustomPermissions).length === 0 && !selectedCustomRole ? (
                    <Box sx={{ textAlign: "center", py: 8, opacity: 0.7 }}>
                        <LockIcon sx={{ fontSize: 64, color: "#1e8bafff", mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, fontWeight: 500 }}>
                            Select a custom role to view permissions
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        {/* CRUD Operations Table */}
                        {(() => {
                            const { crudGroups, otherGroups } = groupPermissionsByResource(tempCustomPermissions);
                            const hasCrud = Object.keys(crudGroups).length > 0;
                            const hasOther = Object.keys(otherGroups).length > 0;

                            return (
                                <>

                                    {hasCrud && (
                                        <Box sx={{ mb: 4 }}>

                                            {/* ===== CRUD HEADER WITH ENABLE ALL ===== */}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    mb: 2,
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color:
                                                            mode === "dark"
                                                                ? palette.dark.headerText
                                                                : mode === "custom"
                                                                    ? customColors?.text || "#111111"
                                                                    : palette.light.headerText,
                                                    }}
                                                >
                                                    CRUD Permissions
                                                </Typography>

                                                <Box sx={{ display: "flex", gap: 2 }}>
                                                    {/* SELECT ALL */}
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={permissionMode === "all"}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    setPermissionMode(checked ? "all" : "custom");
                                                                    toggleAllPermissions(checked);
                                                                }}
                                                                sx={{ '&.Mui-checked': { color: "#4caf50" } }}
                                                            />
                                                        }
                                                        label="Enable All"
                                                    />

                                                    {/* CUSTOM SELECTION */}
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={permissionMode === "custom"}
                                                                onChange={() => setPermissionMode("custom")}
                                                            />
                                                        }
                                                        label="Custom Selection"
                                                    />
                                                </Box>

                                            </Box>

                                            {/* ===== SCROLLABLE CRUD TABLE WITH STICKY HEADER ===== */}
                                            <TableContainer
                                                sx={{
                                                    height: '350px', // Fixed height for scrolling
                                                    overflow: 'auto', // Enable vertical scrolling
                                                    border: mode === "dark"
                                                        ? "1px solid rgba(255,255,255,0.1)"
                                                        : "1px solid rgba(30,139,175,0.15)",
                                                    borderRadius: "12px",
                                                    position: 'relative',
                                                    '&::-webkit-scrollbar': {
                                                        width: '8px',
                                                    },
                                                    '&::-webkit-scrollbar-track': {
                                                        background: mode === "dark"
                                                            ? 'rgba(255,255,255,0.05)'
                                                            : 'rgba(30,139,175,0.05)',
                                                        borderRadius: '4px',
                                                    },
                                                    '&::-webkit-scrollbar-thumb': {
                                                        background: mode === "dark"
                                                            ? 'rgba(255,255,255,0.2)'
                                                            : 'rgba(30,139,175,0.3)',
                                                        borderRadius: '4px',
                                                    },
                                                    '&::-webkit-scrollbar-thumb:hover': {
                                                        background: mode === "dark"
                                                            ? 'rgba(255,255,255,0.3)'
                                                            : 'rgba(30,139,175,0.5)',
                                                    },
                                                }}
                                            >
                                                <Table
                                                    size="small"
                                                    sx={{
                                                        tableLayout: 'fixed',
                                                    }}
                                                >
                                                    <TableHead>
                                                        <TableRow
                                                            sx={{
                                                                background: "linear-gradient(135deg,#1e3a8a,#1e8bafff)",
                                                                position: 'sticky',
                                                                top: 0,
                                                                zIndex: 1,
                                                            }}
                                                        >
                                                            <TableCell sx={{
                                                                color: "#fff",
                                                                fontWeight: 700,
                                                                fontSize: '0.875rem',
                                                                py: 1,
                                                                width: '30%',
                                                                borderRight: '1px solid rgba(255,255,255,0.2)'
                                                            }}>
                                                                RESOURCE
                                                            </TableCell>
                                                            <TableCell align="center" sx={{
                                                                color: "#fff",
                                                                fontWeight: 700,
                                                                fontSize: '0.875rem',
                                                                py: 1,
                                                                width: '17.5%',
                                                                borderRight: '1px solid rgba(255,255,255,0.2)'
                                                            }}>
                                                                CREATE
                                                            </TableCell>
                                                            <TableCell align="center" sx={{
                                                                color: "#fff",
                                                                fontWeight: 700,
                                                                fontSize: '0.875rem',
                                                                py: 1,
                                                                width: '17.5%',
                                                                borderRight: '1px solid rgba(255,255,255,0.2)'
                                                            }}>
                                                                READ
                                                            </TableCell>
                                                            <TableCell align="center" sx={{
                                                                color: "#fff",
                                                                fontWeight: 700,
                                                                fontSize: '0.875rem',
                                                                py: 1,
                                                                width: '17.5%',
                                                                borderRight: '1px solid rgba(255,255,255,0.2)'
                                                            }}>
                                                                UPDATE
                                                            </TableCell>
                                                            <TableCell align="center" sx={{
                                                                color: "#fff",
                                                                fontWeight: 700,
                                                                fontSize: '0.875rem',
                                                                py: 1,
                                                                width: '17.5%'
                                                            }}>
                                                                DELETE
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>

                                                    <TableBody>
                                                        {Object.entries(crudGroups).map(([resource, actions], index) => (
                                                            <TableRow key={resource} hover>
                                                                <TableCell sx={{
                                                                    fontWeight: 600,
                                                                    fontSize: '0.8125rem',
                                                                    py: 1,
                                                                    width: '30%',
                                                                    borderRight: mode === "dark"
                                                                        ? '1px solid rgba(255,255,255,0.1)'
                                                                        : '1px solid rgba(30,139,175,0.15)',
                                                                }}>
                                                                    {resource}
                                                                </TableCell>

                                                                {["create", "read", "update", "delete"].map((action, idx) => (
                                                                    <TableCell
                                                                        key={action}
                                                                        align="center"
                                                                        sx={{
                                                                            py: 1,
                                                                            width: '17.5%',
                                                                            borderRight: idx < 3
                                                                                ? (mode === "dark"
                                                                                    ? '1px solid rgba(255,255,255,0.1)'
                                                                                    : '1px solid rgba(30,139,175,0.15)')
                                                                                : 'none'
                                                                        }}
                                                                    >
                                                                        {actions[action] ? (
                                                                            <Checkbox
                                                                                checked={actions[action].value || false}
                                                                                onChange={handleCustomPermissionChange(actions[action].key)}
                                                                                size="small"
                                                                                sx={{
                                                                                    '&.Mui-checked': { color: "#4caf50" },
                                                                                    padding: '4px'
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>-</Typography>
                                                                        )}
                                                                    </TableCell>
                                                                ))}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}

                                    {/* Other Permissions - Grouped */}
                                    {hasOther && (
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 2,
                                                    color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                                }}
                                            >
                                                Other Permissions
                                            </Typography>
                                            <Box sx={{
                                                height: '250px',
                                                overflow: 'auto',
                                                border: mode === "dark"
                                                    ? "1px solid rgba(255, 255, 255, 0.1)"
                                                    : "1px solid rgba(30, 139, 175, 0.15)",
                                                borderRadius: "8px",
                                                p: 1,
                                                '&::-webkit-scrollbar': {
                                                    width: '6px',
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    background: mode === "dark"
                                                        ? 'rgba(255,255,255,0.05)'
                                                        : 'rgba(30,139,175,0.05)',
                                                    borderRadius: '4px',
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    background: mode === "dark"
                                                        ? 'rgba(255,255,255,0.2)'
                                                        : 'rgba(30,139,175,0.3)',
                                                    borderRadius: '4px',
                                                },
                                            }}>
                                                {Object.entries(otherGroups).map(([resource, actions]) => (
                                                    <Box
                                                        key={resource}
                                                        sx={{
                                                            mb: 2,
                                                            p: 1.5,
                                                            border: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(30, 139, 175, 0.15)",
                                                            borderRadius: "8px",
                                                            backgroundColor: mode === "dark" ? "rgba(30, 139, 175, 0.1)" : "rgba(30, 139, 175, 0.08)",
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                fontWeight: 600,
                                                                mb: 1,
                                                                color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                                                textTransform: 'capitalize',
                                                                fontSize: '0.875rem'
                                                            }}
                                                        >
                                                            {resource}
                                                        </Typography>
                                                        <Grid container spacing={0.5}>
                                                            {Object.entries(actions).map(([action, { key, value }]) => (
                                                                <Grid item xs={6} sm={4} md={3} key={key}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value || false}
                                                                                onChange={handleCustomPermissionChange(key)}
                                                                                size="small"
                                                                                sx={{
                                                                                    color: mode === "dark" ? palette.dark.text : mode === "custom" ? customColors?.text || "#111111" : palette.light.text,
                                                                                    '&.Mui-checked': { color: "#4caf50" },
                                                                                    padding: '4px'
                                                                                }}
                                                                            />
                                                                        }
                                                                        label={
                                                                            <Typography sx={{ fontSize: '0.75rem' }}>
                                                                                {action.charAt(0).toUpperCase() + action.slice(1)}
                                                                            </Typography>
                                                                        }
                                                                        sx={{
                                                                            color: mode === "dark" ? palette.dark.text : mode === "custom" ? customColors?.text || "#111111" : palette.light.text,
                                                                            m: 0
                                                                        }}
                                                                    />
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </>
                            );
                        })()}
                    </Box>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
                    <Button
                        variant="contained"
                        onClick={handleCustomPermissionsUpdate}
                        disabled={!selectedCustomRole || isLoading}
                        sx={{
                            background: !selectedCustomRole || isLoading
                                ? "linear-gradient(135deg, #9ca3af 0%, #cbd5e1 100%)"
                                : "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "1rem",
                            textTransform: "none",
                            px: 4,
                            py: 1.25,
                            borderRadius: "12px",
                            boxShadow: !selectedCustomRole || isLoading
                                ? "none"
                                : "0 4px 14px rgba(76, 175, 80, 0.3)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                                background: !selectedCustomRole || isLoading
                                    ? "linear-gradient(135deg, #9ca3af 0%, #cbd5e1 100%)"
                                    : "linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)",
                                boxShadow: !selectedCustomRole || isLoading
                                    ? "none"
                                    : "0 6px 20px rgba(76, 175, 80, 0.4)",
                                transform: !selectedCustomRole || isLoading ? "none" : "translateY(-2px)",
                            },
                            "&:active": {
                                transform: "translateY(0px)",
                            },
                        }}
                    >
                        Save Permissions
                    </Button>
                </Box>
            </StyledPaper>
        </>
    );
}